import { useCallback, useEffect, useRef, useState } from 'react';
import type { ExternalServiceOperationsSummary } from '../../../shell/externalServiceRepo';
import type { IntelligenceKpisResponse } from '../../domain/reportsIntelligenceTypes';
import {
  formatReportsLoadError,
  formatIntelligenceLoadError,
  loadDashboardData,
  loadIntelligenceData,
  loadRentabilidadData,
  type DashboardData,
} from '../../application/reportsDashboardLoader';
import type { DateRangePreset } from '../reportsScreen.constants';
import type { ReportsQueryScope } from '../../domain/reportsQueryScope';

const DASHBOARD_CACHE_MS = 5 * 60 * 1000;

export type ReportsDashboardFetchParams = {
  currentBranchId: string | null | undefined;
  userId: string | null | undefined;
  dateRangePreset: DateRangePreset;
  customRangeFrom: Date;
  customRangeTo: Date;
  dashboardCacheKey: string;
  reportsQueryScope: ReportsQueryScope;
  customerModuleEnabled: boolean;
  mercadopagoQrMethodAllowed: boolean;
  shouldLoadOperacionDashboard: boolean;
  shouldLoadIntelligence: boolean;
  reportsBootstrapReady: boolean;
};

const EMPTY_DASHBOARD: DashboardData = {
  cashSummary: null,
  todaySalesSnapshot: null,
  periodSalesSnapshot: null,
  previousPeriodSalesSnapshot: null,
  salesDailySeries: null,
  posExceptionSummary: null,
  inventoryDashboard: null,
  salesComparison: null,
  timeBands: [],
  salesByCashier: [],
  promotionPerformance: null,
  customerSummary: null,
  paymentMethods: [],
  externalServiceSummary: null,
  kpiSources: {
    cash: 'device',
    salesRange: 'device',
    comparison: 'device',
    timeBands: 'device',
    cashSessions: 'device',
  },
  branchCashSessions: [],
  salesByCashierSource: 'device',
  lastDashboardLoadAt: '',
};

export function useReportsDashboardFetch({
  currentBranchId,
  userId,
  dateRangePreset,
  customRangeFrom,
  customRangeTo,
  dashboardCacheKey,
  reportsQueryScope,
  customerModuleEnabled,
  mercadopagoQrMethodAllowed: _mercadopagoQrMethodAllowed,
  shouldLoadOperacionDashboard,
  shouldLoadIntelligence,
  reportsBootstrapReady,
}: ReportsDashboardFetchParams) {
  const [data, setData] = useState<DashboardData>(EMPTY_DASHBOARD);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [lastDashboardLoadAt, setLastDashboardLoadAt] = useState<string | null>(null);
  const [periodRentabilidadKpis, setPeriodRentabilidadKpis] = useState<IntelligenceKpisResponse | null>(null);
  const [previousPeriodRentabilidadKpis, setPreviousPeriodRentabilidadKpis] = useState<IntelligenceKpisResponse | null>(null);
  const [intelligenceData, setIntelligenceData] = useState<IntelligenceKpisResponse | null>(null);
  const [intelligenceLoading, setIntelligenceLoading] = useState(false);
  const [intelligenceError, setIntelligenceError] = useState<string | null>(null);
  const [intelligenceErrorShowRetry, setIntelligenceErrorShowRetry] = useState(true);
  const [rentabilidadLoading, setRentabilidadLoading] = useState(false);
  const [rentabilidadError, setRentabilidadError] = useState<string | null>(null);
  const [rentabilidadErrorShowRetry, setRentabilidadErrorShowRetry] = useState(true);
  const [externalServiceSummary, setExternalServiceSummary] = useState<ExternalServiceOperationsSummary | null>(null);

  const dashboardCacheRef = useRef<{ key: string; loadedAt: number } | null>(null);
  const dashboardLoadInFlightRef = useRef<{ key: string; promise: Promise<void> } | null>(null);
  const dashboardLoadGenerationRef = useRef(0);
  const rentabilidadLoadGenerationRef = useRef(0);
  const loadDashboardRef = useRef<(force?: boolean) => Promise<void>>(async () => {});

  const clearDashboardCache = useCallback(() => {
    dashboardCacheRef.current = null;
  }, []);

  const loadDashboard = useCallback(async (force = false) => {
    if (!currentBranchId) {
      setLoading(false);
      setLoadError(null);
      setLastDashboardLoadAt(null);
      setPeriodRentabilidadKpis(null);
      setPreviousPeriodRentabilidadKpis(null);
      setData(EMPTY_DASHBOARD);
      setExternalServiceSummary(null);
      return;
    }

    const cacheKey = dashboardCacheKey;
    if (
      !force &&
      dashboardCacheRef.current?.key === cacheKey &&
      Date.now() - dashboardCacheRef.current.loadedAt < DASHBOARD_CACHE_MS
    ) {
      setLoading(false);
      return;
    }

    if (!force && dashboardLoadInFlightRef.current?.key === cacheKey) {
      return dashboardLoadInFlightRef.current.promise;
    }

    setLoadError(null);
    setLoading(true);
    const loadGeneration = ++dashboardLoadGenerationRef.current;

    const run = (async () => {
      try {
        const result = await loadDashboardData({
          currentBranchId,
          userId,
          dateRangePreset,
          customRangeFrom,
          customRangeTo,
          reportsQueryScope,
          customerModuleEnabled,
        });

        if (loadGeneration !== dashboardLoadGenerationRef.current) return;

        setData(result);
        setExternalServiceSummary(result.externalServiceSummary);
        setLastDashboardLoadAt(result.lastDashboardLoadAt);
        dashboardCacheRef.current = { key: cacheKey, loadedAt: Date.now() };
      } catch (err) {
        if (loadGeneration !== dashboardLoadGenerationRef.current) return;
        setLoadError(formatReportsLoadError(err));
      } finally {
        if (loadGeneration === dashboardLoadGenerationRef.current) {
          setLoading(false);
        }
      }
    })();

    dashboardLoadInFlightRef.current = { key: cacheKey, promise: run };
    try {
      await run;
    } finally {
      if (dashboardLoadInFlightRef.current?.key === cacheKey) {
        dashboardLoadInFlightRef.current = null;
      }
    }
  }, [
    currentBranchId,
    dashboardCacheKey,
    customerModuleEnabled,
    userId,
    dateRangePreset,
    customRangeFrom,
    customRangeTo,
    reportsQueryScope,
  ]);

  loadDashboardRef.current = loadDashboard;

  const loadOperacionRentabilidad = useCallback(async () => {
    if (!currentBranchId) {
      setPeriodRentabilidadKpis(null);
      setPreviousPeriodRentabilidadKpis(null);
      setRentabilidadLoading(false);
      setRentabilidadError(null);
      return;
    }

    const loadGeneration = ++rentabilidadLoadGenerationRef.current;
    setRentabilidadLoading(true);
    setRentabilidadError(null);
    setRentabilidadErrorShowRetry(true);

    try {
      const result = await loadRentabilidadData({
        currentBranchId,
        dateRangePreset,
        customRangeFrom,
        customRangeTo,
        reportsQueryScope,
      });

      if (loadGeneration !== rentabilidadLoadGenerationRef.current) return;

      setPeriodRentabilidadKpis(result.periodRentabilidadKpis);
      setPreviousPeriodRentabilidadKpis(result.previousPeriodRentabilidadKpis);
      setRentabilidadError(null);
    } catch (err) {
      if (loadGeneration !== rentabilidadLoadGenerationRef.current) return;
      console.warn('[Reports] rentabilidad lazy load', err);
      const formatted = formatIntelligenceLoadError(err, 'los datos de margen');
      setRentabilidadError(formatted.message);
      setRentabilidadErrorShowRetry(formatted.showRetry);
      setPeriodRentabilidadKpis(null);
      setPreviousPeriodRentabilidadKpis(null);
    } finally {
      if (loadGeneration === rentabilidadLoadGenerationRef.current) {
        setRentabilidadLoading(false);
      }
    }
  }, [
    currentBranchId,
    dateRangePreset,
    customRangeFrom,
    customRangeTo,
    reportsQueryScope,
  ]);

  useEffect(() => {
    if (!shouldLoadOperacionDashboard || !reportsBootstrapReady) return;
    void loadOperacionRentabilidad();
  }, [shouldLoadOperacionDashboard, reportsBootstrapReady, dashboardCacheKey, loadOperacionRentabilidad]);

  const loadIntelligence = useCallback(async () => {
    if (!currentBranchId) return;
    setIntelligenceLoading(true);
    setIntelligenceError(null);
    setIntelligenceErrorShowRetry(true);
    try {
      const res = await loadIntelligenceData({
        currentBranchId,
        dateRangePreset,
        customRangeFrom,
        customRangeTo,
        reportsQueryScope,
        periodRentabilidadKpis,
      });
      setIntelligenceData(res);
      setIntelligenceError(null);
    } catch (e: unknown) {
      if (e instanceof Error && e.message.includes('necesitan internet')) {
        setIntelligenceError(e.message);
        setIntelligenceErrorShowRetry(false);
      } else {
        const formatted = formatIntelligenceLoadError(e);
        setIntelligenceError(formatted.message);
        setIntelligenceErrorShowRetry(formatted.showRetry);
      }
      setIntelligenceData(null);
    } finally {
      setIntelligenceLoading(false);
    }
  }, [currentBranchId, dateRangePreset, customRangeFrom, customRangeTo, periodRentabilidadKpis, reportsQueryScope]);

  useEffect(() => {
    if (!shouldLoadOperacionDashboard || !reportsBootstrapReady) return;
    void loadDashboardRef.current();
  }, [shouldLoadOperacionDashboard, reportsBootstrapReady, dashboardCacheKey]);

  useEffect(() => {
    if (shouldLoadIntelligence) {
      loadIntelligence().catch(() => {});
    }
  }, [shouldLoadIntelligence, loadIntelligence]);

  return {
    cashSummary: data.cashSummary,
    todaySalesSnapshot: data.todaySalesSnapshot,
    periodSalesSnapshot: data.periodSalesSnapshot,
    previousPeriodSalesSnapshot: data.previousPeriodSalesSnapshot,
    salesDailySeries: data.salesDailySeries,
    posExceptionSummary: data.posExceptionSummary,
    inventoryDashboard: data.inventoryDashboard,
    salesComparison: data.salesComparison,
    timeBands: data.timeBands,
    salesByCashier: data.salesByCashier,
    promotionPerformance: data.promotionPerformance,
    customerSummary: data.customerSummary,
    loading,
    loadError,
    lastDashboardLoadAt,
    paymentMethods: data.paymentMethods,
    externalServiceSummary,
    kpiSources: data.kpiSources,
    branchCashSessions: data.branchCashSessions,
    salesByCashierSource: data.salesByCashierSource,
    periodRentabilidadKpis,
    previousPeriodRentabilidadKpis,
    rentabilidadLoading,
    rentabilidadError,
    rentabilidadErrorShowRetry,
    intelligenceData,
    intelligenceLoading,
    intelligenceError,
    intelligenceErrorShowRetry,
    setExternalServiceSummary,
    loadDashboard,
    loadOperacionRentabilidad,
    loadIntelligence,
    clearDashboardCache,
  };
}
